import type { ContactStatus } from "../types"
import { useTranslation } from "@/hooks/use-translation"

/** Muted source-value hint shown beside the label */
export function AtsHint({ value }: { value: string | null | undefined }) {
  const { t } = useTranslation()
  if (value === undefined) return null
  return (
    <span className="text-xs text-gray-400 leading-none">
      {"["}
      <span className="text-gray-500">{value || t("notAvailable")}</span>
      {"]"}
    </span>
  )
}

export function AtsRateHint({ value, currencyCode }: { value: number | null | undefined; currencyCode?: string | null }) {
  const { t } = useTranslation()
  if (value === undefined) return null
  const formatted = value != null
    ? `${currencyCode ?? ""} ${value.toFixed(2)}`.trim()
    : t("notAvailable")
  return (
    <span className="text-xs text-gray-400 leading-none">
      {"["}
      <span className="text-gray-500">{formatted}</span>
      {"]"}
    </span>
  )
}

/** Neutral "Changed" pill shown when the selected value differs from the source value */
export function ChangedPill() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-200">
      {t("changed")}
    </span>
  )
}

export function useFormatContactStatus(): (status: ContactStatus) => string {
  const { t } = useTranslation()
  return (status: ContactStatus) => {
    switch (status) {
      case "Active":
        return t("statusActive")
      case "Inactive":
        return t("statusInactive")
      case "PendingApproval":
        return t("statusPendingApproval")
      case "PendingChange":
        return t("statusPendingChange")
      case "Terminated":
        return t("statusTerminated")
      default:
        return status
    }
  }
}

export function contactStatusColor(status: ContactStatus): string {
  switch (status) {
    case "Active":
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
    case "Inactive":
      return "bg-gray-100 text-gray-600 ring-gray-500/20"
    case "PendingApproval":
      return "bg-amber-50 text-amber-700 ring-amber-600/20"
    case "PendingChange":
      return "bg-orange-50 text-orange-700 ring-orange-600/20"
    case "Terminated":
      return "bg-red-50 text-red-600 ring-red-500/20"
    default:
      return "bg-gray-100 text-gray-600 ring-gray-500/20"
  }
}
